import type { QuestionnairePage } from '@/lib/questionnaires/Questionnaire'
import { FormikProps, useFormikContext } from 'formik'
import QuestionnaireInput from '@/shared/components/questionnaires/QuestionnaireInput'
import PersonalInformationFormFields from '@/shared/forms/components/PersonalInformationFormFields'
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion'
import clsx from 'clsx'
import * as React from 'react'
import { ReactNode } from 'react'
import { PatientProfileForm } from '@/lib/apis/validators'
import UploadDocumentsContainer, {
  UploadDocumentContainersProps,
} from '@/shared/components/UploadDocumentContainer'
import { SizeVariants } from '@/shared/forms/components/constants'

type Props<T> =
  | {
      page: Exclude<QuestionnairePage, 'upload-supporting-document'>
      formikProps: FormikProps<T>
      disabledProfileFields?: Set<string>

      onChange?: (e: React.ChangeEvent<any>) => void
      onBlur?: (e: React.FocusEvent<any>) => void
      uploadConfig: undefined
    }
  | {
      page: 'upload-supporting-document'
      formikProps: FormikProps<T>
      disabledProfileFields?: Set<string>

      onChange?: (e: React.ChangeEvent<any>) => void
      onBlur?: (e: React.FocusEvent<any>) => void
      uploadConfig: UploadDocumentContainersProps
    }

export default function QuestionnairePage<T>({
  formikProps,
  disabledProfileFields,
  onChange,
  onBlur,
  ...props
}: Props<T>) {
  const { values, setValues } = useFormikContext<PatientProfileForm>()

  const animation: Pick<
    HTMLMotionProps<'div'>,
    'initial' | 'animate' | 'exit' | 'transition'
  > = {
    initial: { x: '3%', opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: '-3%', opacity: 0 },
    transition: { ease: 'easeInOut', duration: 0.3 },
  }

  if (props.page === 'personal-information')
    return (
      <Layout>
        <PersonalInformationFormFields
          values={values}
          setValues={setValues}
          disabledFields={disabledProfileFields}
        />
      </Layout>
    )
  else if (props.page === 'upload-supporting-document')
    return (
      <Layout>
        <div className={SizeVariants['full']}>
          <UploadDocumentsContainer {...props.uploadConfig} />
        </div>
      </Layout>
    )
  else if (props.page === 'call-arrangement') return <p>Not implemented</p>
  else
    return (
      <Layout>
        <AnimatePresence mode="sync">
          {props.page.questions.map((question) => {
            const key = (() => {
              switch (question.type) {
                case 'address':
                case 'date':
                case 'free-text':
                case 'yes-no':
                case 'single-choice':
                case 'yes-no-boolean':
                case 'multiple-choice':
                  return question.key
                case 'duration':
                  return `${question.yearKey}-${question.monthKey}`
                case 'date-range':
                  return `${question.startKey}-${question.endKey}`
              }
            })()

            return (
              <motion.div
                key={key}
                className={clsx(
                  'col-span-1 grid grid-cols-1 gap-x-6 gap-y-6 sm:col-span-6 sm:grid-cols-6',
                  (question.type === 'date' ||
                    question.type === 'date-range' ||
                    question.type === 'address') &&
                    'z-10',
                )}
                {...animation}
              >
                <QuestionnaireInput
                  question={question}
                  formikProps={formikProps}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              </motion.div>
            )
          })}
        </AnimatePresence>
      </Layout>
    )
}

type LayoutProps = {
  children: ReactNode
}

function Layout({ children }: LayoutProps) {
  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-6">
      {children}
    </div>
  )
}
